/**
 * StudyListPage — 学習テキスト一覧 (FR-03/FR-04/FR-12)
 * 経験則 #21: カテゴリフィルタは URL searchParams SSOT
 */

import { useEffect, useState, useContext } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { listStudyTexts, listCategories, getReadProgress } from '../api'
import type { StudyTextSummary, Category, ReadProgressItem, CertificationType } from '../api/types'
import { AuthContext } from '../contexts/AuthContext'
import Icon from '../components/Icon'
import { PageLoader } from '../components/Spinner'

function ViewedIcon({ isViewed }: { isViewed?: boolean }) {
  if (isViewed) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="var(--status-correct-bg)" stroke="none" />
        <path d="M20 6L9 17l-5-5" stroke="var(--status-correct-color)" />
      </svg>
    )
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--status-unstudied-color)" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
    </svg>
  )
}

export default function StudyListPage() {
  const { certType } = useContext(AuthContext)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // #21: フィルタ状態は URL searchParams SSOT
  const selectedCategoryId = searchParams.get('category_id') ?? ''

  const [texts, setTexts] = useState<StudyTextSummary[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [readProgress, setReadProgress] = useState<ReadProgressItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!certType) return
    const ct = certType as CertificationType
    setLoading(true)
    Promise.all([
      listStudyTexts({ certification_type: ct, category_id: selectedCategoryId || undefined }),
      listCategories(ct),
      getReadProgress(ct),
    ])
      .then(([txts, cats, rp]) => {
        setTexts(txts?.study_texts ?? [])
        setCategories(cats?.categories ?? [])
        setReadProgress(rp?.read_progress ?? [])
      })
      .finally(() => setLoading(false))
  }, [certType, selectedCategoryId])

  const handleCategoryChange = (catId: string) => {
    const next = new URLSearchParams(searchParams)
    if (catId) next.set('category_id', catId)
    else next.delete('category_id')
    setSearchParams(next, { replace: true })
  }

  const currentProgress = selectedCategoryId
    ? readProgress.find((r) => r.category_id === selectedCategoryId)
    : null

  const chipCls = (active: boolean) =>
    [
      'whitespace-nowrap rounded-pill px-3.5 py-1.5 text-sm transition-colors duration-fast',
      active
        ? 'border-2 border-accent bg-accent-subtle font-bold text-accent'
        : 'border border-border-strong bg-surface font-medium text-text-secondary hover:border-accent',
    ].join(' ')

  return (
    <div className="mx-auto max-w-content p-screen">
      <h1 className="mb-4 text-[22px] font-bold tracking-tight text-text-primary">学習テキスト</h1>

      {/* カテゴリフィルタ (#21 URL SSOT) */}
      <div data-testid="category-filter" className="mb-5 overflow-x-auto" role="group" aria-label="カテゴリで絞り込み">
        <div className="flex w-max gap-2 pb-1">
          <button onClick={() => handleCategoryChange('')} aria-pressed={!selectedCategoryId} className={chipCls(!selectedCategoryId)}>
            すべて
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => handleCategoryChange(cat.id ?? '')}
              aria-pressed={selectedCategoryId === cat.id}
              className={chipCls(selectedCategoryId === cat.id)}
            >
              {cat.name_ja}
            </button>
          ))}
        </div>
      </div>

      {/* カテゴリ別閲覧進捗 (FR-04) */}
      {currentProgress && (
        <div data-testid="read-progress" className="mb-4 rounded-md border border-border bg-surface px-3.5 py-2.5">
          <div className="mb-1.5 flex justify-between text-[13px]">
            <span className="text-text-secondary">閲覧済み</span>
            <span className="font-bold">
              {currentProgress.viewed_texts}/{currentProgress.total_texts}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-pill bg-surface-alt">
            <div className="h-full rounded-pill bg-accent" style={{ width: `${Math.round((currentProgress.viewed_ratio ?? 0) * 100)}%` }} />
          </div>
        </div>
      )}

      {loading ? (
        <PageLoader />
      ) : texts.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface px-6 py-10 text-center text-text-muted">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-surface-alt text-text-muted">
            <Icon name="book" size={24} />
          </div>
          <div className="font-semibold text-text-primary">テキストがありません</div>
          <div className="mt-1 text-sm">このカテゴリのテキストは準備中です</div>
        </div>
      ) : (
        <div data-testid="text-list" className="overflow-hidden rounded-lg border border-border bg-surface">
          {texts.map((txt, idx) => (
            <button
              key={txt.id}
              data-testid="text-list-item"
              onClick={() => navigate(`/study/${txt.id}`)}
              className={[
                'flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-surface-alt',
                idx < texts.length - 1 ? 'border-b border-border' : '',
              ].join(' ')}
            >
              <ViewedIcon isViewed={txt.is_viewed} />
              <span className="flex-1 text-[15px] text-text-primary">{txt.title}</span>
              <span className="text-xs text-text-muted">{txt.is_viewed ? '閲覧済み' : '未閲覧'}</span>
              <Icon name="chevron-right" size={16} className="text-text-muted" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
